import { useEffect, useState } from "react";
import { useForm as hooksUseForm } from "react-hook-form";
import { useUniqueId } from "../../../../hooks/useUniqueId";
import { extractFormErrors, formHasFieldName } from "./lib";
import { useFieldErrorParams } from "./useFieldErrorParams";
/**
 * This hooks enforces some defaults for `useForm` of `react-hook-form` and
 * gives you an API to handle microservice errors
 */
export const useForm = (options) => {
    const { onSubmit, onAfterSubmit, onAfterFailedSubmit, defaultValues, resetFormOnSuccessWithNewValues = true, disabled, } = options;
    const id = useUniqueId();
    const fieldErrorParamsController = useFieldErrorParams();
    const [submitIsDisabledState, setSubmitIsDisabled] = useState(false);
    const [isDisabledByState, setIsDisabled] = useState(false);
    const form = hooksUseForm({
        mode: "onSubmit",
        reValidateMode: "onChange",
        ...options,
    });
    const submitIsDisabled = submitIsDisabledState ||
        form.formState.isSubmitting ||
        (form.formState.isSubmitted &&
            !form.formState.isSubmitSuccessful &&
            !form.formState.isValid);
    /**
     * Custom Reset Method This fixes the current missing react-form-hook reset on
     * unnested forms
     * https://github.com/react-hook-form/react-hook-form/pull/4829
     */
    const reset = (values, keepStateOptions = {}) => {
        const { shouldFocus = false, ...restKeepStateOptions } = keepStateOptions;
        const formToReset = document.getElementById(id);
        if (formToReset instanceof HTMLFormElement) {
            formToReset.reset();
            if (shouldFocus) {
                const firstElement = formToReset.elements.item(0);
                if (firstElement &&
                    "focus" in firstElement &&
                    typeof firstElement["focus"] === "function") {
                    firstElement.focus();
                }
            }
        }
        form.reset(values, {
            ...restKeepStateOptions,
        });
    };
    /**
     * The useForm hook from `react-hook-form` does not support changing the
     * default values. This is ok, since this isn't usual. Think of changing the
     * default values on already set fields. Should they change, or not? But we do
     * have the following common use case: Editing something, which has to be
     * fetched from the API. While the data to be edited is still loading, the
     * user should already see the form - ideal disabled, to avoid interacting
     * with the unready form. When the data is ready, the inputs should be filled
     * and the form be enabled. Instead of changing the default values this hook
     * just resets the form values to the default values, if defaultValues are
     * changing from "not set" to "set" (see dependencies).
     *
     * Changing a single default value is intentional not supported!
     */
    useEffect(() => {
        if (!defaultValues) {
            return;
        }
        reset(defaultValues, {
            keepIsValid: true,
            shouldFocus: false,
            keepDefaultValues: false,
            keepDirty: false,
            keepTouched: false,
            keepErrors: false,
            keepSubmitCount: false,
            keepIsSubmitted: false,
        });
    }, [!!defaultValues]);
    let shouldTriggerOnAfterSubmit = false;
    const handleAfterSubmit = (result, error) => {
        const submitHandlerFailed = error !== undefined;
        if (submitHandlerFailed && onAfterFailedSubmit) {
            onAfterFailedSubmit(result, error);
            return;
        }
        if (!submitHandlerFailed && shouldTriggerOnAfterSubmit && onAfterSubmit) {
            onAfterSubmit(result);
        }
    };
    const handleError = (error) => {
        try {
            let isErrorAssignableToField = false;
            const formErrors = extractFormErrors(error);
            const formHasErrors = formErrors.length > 0;
            if (formHasErrors) {
                for (const validationError of formErrors) {
                    if (!validationError.path ||
                        !validationError.type ||
                        !formHasFieldName(form, validationError.path)) {
                        continue;
                    }
                    const path = validationError.path;
                    const { params, type } = validationError;
                    // do not show error modal, if error can be assigned to a certain form field
                    isErrorAssignableToField = true;
                    if (params) {
                        fieldErrorParamsController.update(path, params);
                    }
                    form.setError(path, {
                        type,
                    });
                }
            }
            if (!isErrorAssignableToField) {
                throw error;
            }
        }
        catch (errorHandlerError) {
            throw error;
        }
    };
    const submit = (event) => {
        if (submitIsDisabled) {
            return;
        }
        const handleSubmitWrapper = new Promise((resolve, reject) => {
            let submitWrapperResult;
            const handleFormInvalid = () => {
                submitWrapperResult = false;
            };
            const handleSubmit = form.handleSubmit(async (fieldValues) => {
                if (event?.preventSubmit) {
                    // form.trigger() checks if form is valid
                    submitWrapperResult = await form.trigger();
                    return;
                }
                try {
                    const submitResult = await onSubmit(fieldValues, event);
                    const submitSuccess = submitResult !== false;
                    if (submitSuccess) {
                        submitWrapperResult = submitResult?.result ?? true;
                        shouldTriggerOnAfterSubmit = true;
                        if (resetFormOnSuccessWithNewValues) {
                            reset(fieldValues, {
                                keepDefaultValues: false,
                                keepIsValid: true,
                                keepSubmitCount: true,
                                keepIsSubmitted: true,
                                keepDirty: false,
                                keepErrors: false,
                                keepTouched: false,
                                shouldFocus: false,
                            });
                        }
                    }
                    else {
                        submitWrapperResult = false;
                    }
                }
                catch (e) {
                    reject(e);
                }
            }, handleFormInvalid);
            void handleSubmit()
                .then(() => {
                resolve(submitWrapperResult);
            })
                .catch((error) => {
                reject(error);
            });
        });
        return handleSubmitWrapper
            .then((value) => {
            handleAfterSubmit(value);
            return value;
        })
            .catch((error) => {
            handleAfterSubmit(undefined, error);
            handleError(error);
            return false;
        });
    };
    const confirmationModalOptions = options;
    return {
        ...form,
        reset,
        id,
        submit,
        fieldErrorParamsController,
        disabled: disabled || isDisabledByState,
        submitIsDisabled,
        disableSubmit: setSubmitIsDisabled,
        disableForm: setIsDisabled,
        ...confirmationModalOptions,
    };
};
//# sourceMappingURL=data:application/json;base64,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